import type { ReactNode } from 'react'

import { cn, SUBTITLE_CLASS } from '@/components/base'
import { GroupLabel } from '@/components/GroupLabel'

/**
 * What names a group: the label, the sentence under it, a badge on the same
 * line, and the control the group is operated by.
 *
 * One component for the flat groups of a settings sheet and the framed ones
 * inside a `Panel`.
 */
export function GroupHeading({
  title,
  subtitle,
  badge,
  action,
  reserveAction = false,
  className,
}: {
  title: string
  /** A sentence under the heading, where the group needs one to be read. */
  subtitle?: string
  /** On the heading's line: what the group is, in one word. */
  badge?: ReactNode
  /** At the right end: what the group amounts to, or a control over it. */
  action?: ReactNode
  /**
   * Holds the height of an `action` whether or not this heading carries one,
   * for a row of groups where only some do — without it their bodies start on
   * different lines. The height is `SegmentedControl`'s internal skin, the
   * tallest control that sits in the slot.
   */
  reserveAction?: boolean
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-row items-start gap-3',
        reserveAction && 'min-h-8',
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-row items-center gap-2">
          <GroupLabel>{title}</GroupLabel>
          {badge}
        </div>
        {subtitle ? (
          <p className={cn('mt-0.5', SUBTITLE_CLASS)}>{subtitle}</p>
        ) : null}
      </div>
      {action}
    </div>
  )
}
